import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { projectService, Project } from '../../services/project.service';
import { advanceService, Advance } from '../../services/advance.service';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n || 0);
}

function formatDate(ts?: number): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [advances, setAdvances] = useState<Advance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [proj, adv] = await Promise.all([
        projectService.getProject(id),
        advanceService.getAdvances(id).catch(() => [] as Advance[]),
      ]);
      setProject(proj);
      setAdvances(adv);
    } catch (e: any) {
      setError(e.message || 'Error al cargar proyecto');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  if (loading) return <LoadingState message="Cargando proyecto…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!project) return <ErrorState message="Proyecto no encontrado" onRetry={load} />;

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 flex items-center gap-1"
      >
        ← Volver
      </button>

      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900">{project.name}</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {project.client_name ?? project.client_id.slice(0, 8)}
            </p>
          </div>
          <StatusBadge status={project.status} />
        </div>

        {typeof project.progress === 'number' && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Avance</span>
              <span className="font-bold text-slate-900">{project.progress}%</span>
            </div>
            <ProgressBar value={project.progress} />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm pt-2">
          <div>
            <p className="text-xs text-slate-500">Presupuesto</p>
            <p className="font-bold text-primary">{formatCurrency(project.budget)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Dirección</p>
            <p className="text-slate-800 truncate">{project.address ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Inicio</p>
            <p className="text-slate-800">{formatDate(project.start_date)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Fin</p>
            <p className="text-slate-800">{formatDate(project.end_date)}</p>
          </div>
        </div>

        {project.description && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-1">Descripción</p>
            <p className="text-sm text-slate-700 leading-relaxed">{project.description}</p>
          </div>
        )}
      </div>

      <section className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-slate-900">Avances ({advances.length})</h2>
          <Link
            to="/gestion/avance-obra"
            className="text-xs text-primary font-semibold"
          >
            + Registrar
          </Link>
        </div>
        {advances.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">Sin avances registrados</p>
        ) : (
          <ul className="space-y-2">
            {advances.slice(0, 8).map((a) => (
              <li key={a.id}>
                <Link
                  to={`/gestion/avances/${a.id}`}
                  className="block active:bg-slate-50 rounded-lg -mx-2 px-2 py-2"
                >
                  <div className="flex justify-between items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-slate-900">
                      Semana {a.week_number} · {a.year}
                    </p>
                    <StatusBadge status={a.status} />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>{a.progress_percentage}% avance</span>
                    {a.contractor_name && (
                      <>
                        <span>·</span>
                        <span className="truncate">{a.contractor_name}</span>
                      </>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}